import { signInWithEmailAndPassword } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../config';
import { getUserData } from './user';
import { validateLogin } from './validation/validators';
import { handleAuthError } from './handlers/authError';
import type { AuthResult } from './types';

// Helper function to check if input is a phone number (starts with 05 and has 10 digits)
function isPhoneNumber(input: string): boolean {
  return /^05\d{8}$/.test(input);
}

// Helper function to find user email by phone number
async function findEmailByPhone(phone: string): Promise<string> {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('phone', '==', phone));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('No user found with this phone number');
    }
    
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();
    
    if (!userData.email) {
      throw new Error('User email not found');
    }
    
    return userData.email;
  } catch (error: any) {
    console.error('Error finding user by phone:', error);
    throw new Error('Failed to find user with this phone number');
  }
}

export async function loginUser(emailOrPhone: string, password: string): Promise<AuthResult> {
  try {
    let email = emailOrPhone;
    
    // If input is a phone number, find the corresponding email
    if (isPhoneNumber(emailOrPhone)) {
      email = await findEmailByPhone(emailOrPhone);
    }

    // Validate input (now with the resolved email)
    const validatedInput = validateLogin({ email, password });

    // Authenticate
    const { user } = await signInWithEmailAndPassword(
      auth,
      validatedInput.email,
      validatedInput.password
    );

    // Get user data
    const userData = await getUserData(user.uid);
    if (!userData) {
      throw new Error('User data not found');
    }

    return { user, userData };
  } catch (error) {
    throw handleAuthError(error);
  }
}